"use client"

import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"

export interface DisplayAuthor {
  slug: string
  name: string
  role: string
  photo: string
  articleCount: number
}

export default function AuthorsClient({
  initialAuthors,
}: {
  initialAuthors: DisplayAuthor[]
}) {
  const lp = useLocalePath()
  const t = useTranslations("ui")

  return (
    <section className="px-6 pt-12 pb-20 md:pt-20">
      <div className="mx-auto max-w-7xl">
        <div>
          <p className="type-body-sm font-mono font-medium tracking-[0.15em] text-neutral-500 uppercase">
            {t("authorsLabel")}
          </p>
          <h1 className="text-aubergine-900 mt-3 text-4xl font-normal md:text-5xl">
            {t("authorsPageHeading")}
          </h1>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {initialAuthors.map((author) => (
            <div key={author.slug} className="flex flex-col">
              <Link
                href={lp(`/authors/${author.slug}`)}
                className="flex h-full flex-col overflow-hidden rounded-[16px] bg-white transition-transform hover:-translate-y-1"
              >
                <div className="relative flex aspect-square items-center justify-center bg-neutral-100">
                  {author.photo ? (
                    <Image
                      src={author.photo}
                      alt={author.name}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      className="object-cover object-top"
                    />
                  ) : (
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-neutral-200 text-2xl font-medium text-neutral-400">
                      {author.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="text-aubergine-900 text-lg font-medium">
                    {author.name}
                  </h2>
                  <p className="mt-1 text-sm text-neutral-500">{author.role}</p>
                  <p className="mt-auto pt-2 text-xs text-neutral-400">
                    {t("authorArticleCount", { count: author.articleCount })}
                  </p>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
